import React, { useState } from 'react';
import { AppState, ModuleId } from './types';
import Header from './components/Header';
import Sidebar from './components/Sidebar';
import ModuleView from './components/ModuleView';
import Login from './components/Login';
import VoiceMascot from './components/VoiceMascot';

const App: React.FC = () => {
  const [state, setState] = useState<AppState>({
    isAuthenticated: false,
    currentModule: null,
    moduleData: null,
    loading: false,
  });
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  const handleLogin = (username: string, password: string): boolean => {
    // ⚠️ DEMO AUTHENTICATION ONLY - NOT FOR PRODUCTION USE ⚠️
    // These credentials are hardcoded for demonstration purposes only.
    // This application contains no sensitive data and is intended for demo use.
    // In production, implement proper authentication (Firebase Auth, Auth0, etc.)
    if (username === 'admin' && password === '3LGYV2pNMXqKVxUs') {
      setState(prev => ({ ...prev, isAuthenticated: true }));
      return true;
    }
    return false;
  };

  const handleLogout = () => {
    setState({
      isAuthenticated: false,
      currentModule: null,
      moduleData: null,
      loading: false,
    });
  };

  const handleModuleSelect = (moduleId: ModuleId) => {
    setState(prev => ({
      ...prev,
      currentModule: moduleId,
      moduleData: null,
    }));
  };

  const handleOptimizationComplete = (data: any) => {
    setState(prev => ({
      ...prev,
      moduleData: data,
      loading: false,
    }));
  };

  const handleLoadingStart = () => {
    setState(prev => ({ ...prev, loading: true }));
  };

  const handleVoiceQuery = (query: string) => {
    console.log('Voice query received:', query);

    // Convert query to lowercase for easier matching
    const lowerQuery = query.toLowerCase();

    // Route to appropriate module based on query content
    if (lowerQuery.includes('cash') || lowerQuery.includes('liquidity') || lowerQuery.includes('treasury')) {
      handleModuleSelect('cashflow');
    } else if (lowerQuery.includes('payable') || lowerQuery.includes('payment') || lowerQuery.includes('discount') || lowerQuery.includes('dpo')) {
      handleModuleSelect('payables');
    } else if (lowerQuery.includes('inventory') || lowerQuery.includes('stock') || lowerQuery.includes('sku')) {
      handleModuleSelect('inventory');
    } else if (lowerQuery.includes('debt') || lowerQuery.includes('refinanc') || lowerQuery.includes('covenant')) {
      handleModuleSelect('debt');
    } else if (lowerQuery.includes('expense') || lowerQuery.includes('anomaly') || lowerQuery.includes('fraud')) {
      handleModuleSelect('expenses');
    } else if (lowerQuery.includes('scenario') || lowerQuery.includes('simulation') || lowerQuery.includes('monte carlo')) {
      handleModuleSelect('scenario');
    } else if (lowerQuery.includes('tax') || lowerQuery.includes('etr') || lowerQuery.includes('beps')) {
      handleModuleSelect('tax');
    } else if (lowerQuery.includes('competitive') || lowerQuery.includes('market') || lowerQuery.includes('pricing')) {
      handleModuleSelect('competitive');
    }

    // You can extend this to trigger specific actions within modules
    // For example: starting an optimization, showing specific data, etc.
  };

  if (!state.isAuthenticated) {
    return <Login onLogin={handleLogin} />;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header onLogout={handleLogout} />
      <div className="flex">
        <Sidebar
          currentModule={state.currentModule}
          onModuleSelect={handleModuleSelect}
          collapsed={sidebarCollapsed}
          onToggle={() => setSidebarCollapsed((c) => !c)}
        />
        <main className={`flex-1 p-8 transition-all duration-300 ease-in-out ${sidebarCollapsed ? 'ml-16' : 'ml-72'}`}>
          <ModuleView
            moduleId={state.currentModule}
            moduleData={state.moduleData}
            loading={state.loading}
            onOptimizationComplete={handleOptimizationComplete}
            onLoadingStart={handleLoadingStart}
          />
        </main>
      </div>

      {/* Voice Assistant Mascot */}
      <VoiceMascot
        currentModule={state.currentModule || 'dashboard'}
        onQuerySubmit={handleVoiceQuery}
        position="bottom-right"
      />
    </div>
  );
};

export default App;
